import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import InstallBanner from '../../components/InstallBanner';

export default function MemberDashboard() {
  const { user }    = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { axios.get('/member/dashboard').then(r => setData(r.data)); }, []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const daysLeft = expiry => expiry ? Math.ceil((new Date(expiry) - new Date()) / 86400000) : null;

  if (!data) return <div className="loading-wrap"><div className="spinner"></div></div>;

  const days = daysLeft(data.membership?.expiry_date);

  return (
    <div>
      {/* PWA install prompt — only shows when not installed */}
      <InstallBanner />

      <div className="page-header">
        <div>
          <h1 className="page-title">{greeting}, {user?.name?.split(' ')[0]} 👋</h1>
          <p className="page-sub">{new Date().toLocaleDateString('en-GB', { weekday:'long', day:'numeric', month:'long' })}</p>
        </div>
        <Link to="/checkin" className="btn-primary">📲 Check in</Link>
      </div>

      {/* Membership status banner */}
      {data.membership ? (
        <div style={{ background: days < 7 ? 'var(--warning-dim)' : 'var(--success-dim)', border:`1px solid ${days < 7 ? 'rgba(245,158,11,.3)' : 'rgba(34,197,94,.3)'}`, borderRadius:'var(--radius-lg)', padding:'14px 18px', marginBottom:20, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
          <div>
            <div style={{ fontSize:14, fontWeight:600, color: days < 7 ? 'var(--warning)' : 'var(--success)' }}>
              {days < 7 ? '⚠️ Membership expiring soon!' : '✅ Membership active'}
            </div>
            <div style={{ fontSize:12, color:'var(--text-secondary)', marginTop:3 }}>
              {data.membership.plan_name} · Expires {new Date(data.membership.expiry_date).toLocaleDateString('en-GB')} ({days} days left)
            </div>
          </div>
          {days < 7 && <div style={{ fontSize:13, color:'var(--warning)', fontWeight:500 }}>Visit the desk to renew →</div>}
        </div>
      ) : (
        <div style={{ background:'var(--danger-dim)', border:'1px solid rgba(239,68,68,.25)', borderRadius:'var(--radius-lg)', padding:'14px 18px', marginBottom:20 }}>
          <div style={{ fontSize:14, fontWeight:600, color:'var(--danger)' }}>❌ No active membership</div>
          <div style={{ fontSize:12, color:'var(--text-secondary)', marginTop:3 }}>Please visit the gym desk to purchase a membership plan.</div>
        </div>
      )}

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:12, marginBottom:20 }}>
        {[
          { icon:'🔥', label:'Day streak', value: data.streak, sub:'Last 30 days' },
          { icon:'📅', label:"Today's check-in", value: data.today_checked_in ? 'Done ✓' : 'Not yet', sub: data.today_checked_in ? 'Great work!' : 'Tap check-in' },
          { icon:'🍽️', label:'Calories today', value: `${Math.round(data.calories_today || 0)} kcal`, sub:'Logged meals' },
          { icon:'💪', label:'Workout plan', value: data.current_workout ? 'Active' : 'Not set', sub: data.current_workout?.title || 'Ask your trainer' },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding:16 }}>
            <div style={{ fontSize:22, marginBottom:8 }}>{s.icon}</div>
            <div style={{ fontSize:11, color:'var(--text-muted)', fontWeight:600, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:4 }}>{s.label}</div>
            <div style={{ fontFamily:'var(--font-display)', fontSize:17, fontWeight:700 }}>{s.value}</div>
            <div style={{ fontSize:11, color:'var(--text-secondary)', marginTop:3 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Quick links */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:10, marginBottom:20 }}>
        {[
          { to:'/workouts',  icon:'🏋️', label:'My workout' },
          { to:'/nutrition', icon:'🥗', label:'Log meal' },
          { to:'/progress',  icon:'📸', label:'Progress photos' },
          { to:'/classes',   icon:'🗓️', label:'Book a class' },
        ].map(l => (
          <Link key={l.to} to={l.to} style={{ display:'flex', alignItems:'center', gap:12, padding:'14px 16px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', textDecoration:'none', transition:'.12s' }}
            onMouseEnter={e => e.currentTarget.style.borderColor='var(--accent-border)'}
            onMouseLeave={e => e.currentTarget.style.borderColor='var(--border)'}>
            <span style={{ fontSize:24 }}>{l.icon}</span>
            <span style={{ fontSize:13, fontWeight:500, color:'var(--text-secondary)' }}>{l.label}</span>
          </Link>
        ))}
      </div>

      {/* Announcements */}
      {data.announcements?.length > 0 && (
        <div className="card">
          <div className="card-header"><div className="card-title">📢 Gym announcements</div></div>
          <div style={{ padding:'6px 0' }}>
            {data.announcements.map(a => (
              <div key={a.id} style={{ padding:'12px 18px', borderBottom:'1px solid var(--border)' }}>
                <div style={{ fontSize:13, fontWeight:600, marginBottom:4 }}>{a.title}</div>
                <div style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:1.6 }}>{a.body}</div>
                <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:6 }}>{new Date(a.created_at).toLocaleDateString('en-GB', { day:'2-digit', month:'short' })}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
